import { useCallback, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import useApiPatch from 'Pods/Shared/Hooks/useApiPatch'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import { patchSessionSettingsInLibraryCache } from 'Pods/ThreeDStudio/Shared/Utils/patchSessionSettingsInLibraryCache'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setOpenedModelSettingsPatch } from 'Pods/ThreeDStudio/Store/actions/ui'

const GENERIC_ERROR_MESSAGE = 'Failed to save viewer settings.'

const showError = (error) => {
  if (typeof window === 'undefined' || !window.showNotification) return
  const message = error?.response?.data?.errors?.[0] || GENERIC_ERROR_MESSAGE
  window.showNotification(message, { type: 'error', autohide: true })
}

const camelToSnake = (key) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)

const toSnakeBody = (camelPatch) => {
  const out = {}
  Object.keys(camelPatch).forEach((k) => { out[camelToSnake(k)] = camelPatch[k] })
  return out
}

/**
 * PATCHes /api/internal/generator/sessions/{id}/settings with a partial body
 * and keeps zustand + React Query caches in sync. Callers pass camelCase keys;
 * the hook converts them to snake_case at the wire boundary only.
 *
 *   updateSessionSettings({ showGrid: true })                    // immediate
 *   updateSessionSettings({ rotation: 45 }, { debounceMs: 300 }) // debounced
 *
 * A debounced call cancels any previously-pending debounced call. The
 * sessionId captured when the call was made is the one the PATCH targets,
 * even if the active session changes before the timer fires.
 */
function useUpdateSessionSettings() {
  const queryClient = useQueryClient()
  const { patchData } = useApiPatch()
  const { captureException } = useSentry()
  const sessionId = useThreeDStudioStore(state => state.generation?.generatorSessionId)
  const isReadOnly = useOpenedModelReadOnly()
  const timerRef = useRef(null)

  // Read read-only state through a ref so both the call-time guard and the
  // deferred sendPatch see the latest value. isReadOnly starts false until
  // pageConfig loads, so a debounced call scheduled in that window must be
  // re-checked when its timer fires - by then a logged-out/non-owner viewer is
  // known and the owner-scoped, auth-only PATCH must be suppressed.
  const isReadOnlyRef = useRef(isReadOnly)
  isReadOnlyRef.current = isReadOnly

  return useCallback((partial, { debounceMs = 0 } = {}) => {
    if (isReadOnlyRef.current) return
    if (!partial || typeof partial !== 'object' || Array.isArray(partial)) return
    if (Object.keys(partial).length === 0 || sessionId == null) return

    const sendPatch = () => {
      if (isReadOnlyRef.current) {
        return
      }

      patchData({
        path: `/api/internal/generator/sessions/${sessionId}/settings`,
        data: { data: toSnakeBody(partial) },
        onSuccess: () => {
          patchSessionSettingsInLibraryCache(queryClient, { sessionId, settingsPatch: partial })
          setOpenedModelSettingsPatch(partial)
        },
        onError: (error) => {
          captureException(error)
          showError(error)
        },
      })
    }

    if (debounceMs > 0) {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(sendPatch, debounceMs)
      return
    }

    sendPatch()
  }, [sessionId, patchData, queryClient, captureException])
}

export default useUpdateSessionSettings
